import { useCallback, useRef, useState } from 'react';
import { getDiscoverVibe } from '../../api/discover';
import { hasUnmatchedCriteria } from '../../helpers/discover_helpers';

// Must match Modal's published grid — misaligned floor/ceiling → HTTP 422.
export const VIBE_BUCKET_WIDTH = 0.05;
const VIBE_TOP_K_PER_BUCKET = 200;
// Cap concurrent Modal calls. At floor=0 there are 20 buckets — without a
// cap that's 20 simultaneous requests, which risks Modal rate-limiting.
const VIBE_CHUNK_CONCURRENCY = 5;

const alignToGrid = v => Math.round(v / VIBE_BUCKET_WIDTH) * VIBE_BUCKET_WIDTH;
const canon = v => Number(v.toFixed(2));

// Per-bucket chunks spanning [floor, ceiling], highest first.
const computeVibeChunks = (floor, ceiling) => {
  const lo = alignToGrid(floor);
  const hi = alignToGrid(ceiling);
  const chunks = [];
  for (let top = hi; top - lo > 1e-9; top -= VIBE_BUCKET_WIDTH) {
    const bottom = top - VIBE_BUCKET_WIDTH;
    chunks.push({ floor: canon(bottom), ceiling: canon(top) });
  }
  return chunks;
};

// Mirrors server-side applyDiscoverSortBy for fields the vibe SELECT returns.
// Adding/changing a sort here must be kept in lockstep with
// node/utils/db/discover.js's applyDiscoverSortBy. The keys are exercised by
// the parity test in useVibeSearch.test.js — any new dropdown sort must
// either add a comparator here or appear in that test's allowlist of sorts
// intentionally not local-sortable in vibe mode.
const followerCount = c => (c.instagramCount || 0) + (c.youtubeCount || 0) + (c.tiktokCount || 0);
const descBy = key => (a, b) => (b[key] || 0) - (a[key] || 0);
export const VIBE_SORTERS = {
  vibe: (a, b) => (b.vibeScore || 0) - (a.vibeScore || 0),
  affinity: (a, b) => (b.categoryAffinity || 0) - (a.categoryAffinity || 0) || a.id - b.id,
  'up-and-coming': (a, b) => (b.upAndComingScore || 0) - (a.upAndComingScore || 0) || a.id - b.id,
  score: (a, b) => Math.round(b.score || 0) - Math.round(a.score || 0) || (b.weeklyVolume || 0) - (a.weeklyVolume || 0),
  followers: (a, b) => followerCount(b) - followerCount(a),
  'followers-instagram': descBy('instagramCount'),
  'followers-tiktok': descBy('tiktokCount'),
  'followers-youtube': descBy('youtubeCount'),
  'followers-substack': descBy('substackCount'),
  volume: descBy('peakMonthlyVolume'),
  traffic: descBy('monthlyPinClicks'),
  orders: descBy('totalOrders'),
  gmv: descBy('totalVolume'),
  'gmv-monthly': descBy('monthlyVolume'),
  'gmv-weekly': descBy('weeklyVolume'),
};

// Unlisted sorts fall back to vibe order.
const sortVibeResults = (results, nextSortBy) => {
  const cmp = VIBE_SORTERS[nextSortBy] || VIBE_SORTERS.vibe;
  return [...results].sort(cmp);
};

// Encapsulates the Discover vibe-search lifecycle: Modal fan-out, progressive
// merge, refill on widened threshold, retry on partial failure, and the local
// filter/sort pipeline that lets the consumer compose dropdowns without re-
// hitting Modal.
//
// The consumer owns page-level display state (creators / page / noMoreResults
// / searchLoading) and receives updates through onViewChange / onLoadingChange.
export const useVibeSearch = ({
  brand,
  fetchIdRef,
  uiStateRef,
  selectedTags,
  selectedLists,
  onViewChange,
  onLoadingChange,
}) => {
  // Modal-ordered candidate set, append-only across chunks. Source of truth.
  const originalRef = useRef([]);
  // Filtered + re-sorted view — what the consumer's loadMore paginates from.
  const allResultsRef = useRef([]);
  // Monotone — lowest affinity floor we've covered. Lowering the dropdown
  // below this triggers a delta-refill for the missing range.
  const loadedFloorRef = useRef(1.0);
  // Captured submitted query so refills don't use a typed-but-not-submitted
  // value from the search input.
  const activeQueryRef = useRef(null);
  // Response-level evidence (taste topic + geo) shared by every hit. Captured
  // write-once from the first successful chunk — Modal returns identical
  // set-level disambiguation across buckets for a given query, so first wins.
  const disambiguationRef = useRef(null);

  const [error, setError] = useState(null);
  const [loadingChunks, setLoadingChunks] = useState(0);
  const [failedChunks, setFailedChunks] = useState([]);
  // Forces a consumer re-render whenever a ref mutation changes one of the
  // values we expose (isActive, loadedFloor, loadedCount). State updates from
  // the chunk pipeline (loadingChunks / error / failedChunks) cover most
  // re-render needs; this bumps for the few that they don't.
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick(t => t + 1), []);

  const reset = useCallback(({ floor = 1.0, query = null } = {}) => {
    originalRef.current = [];
    allResultsRef.current = [];
    loadedFloorRef.current = floor;
    activeQueryRef.current = query;
    // Clear captured disambiguation so a re-search / failed search never renders
    // stale set-level clauses from the prior query.
    disambiguationRef.current = null;
    setFailedChunks([]);
    setError(null);
    bump();
  }, [bump]);

  // Always recomputes from originalRef so filter changes compose
  // (loosening restores rows; tightening narrows).
  const recompute = useCallback(({ sortBy, minAffinity, segments, hidePartialMatches }) => {
    let view = originalRef.current;
    if (minAffinity != null) {
      view = view.filter(c => (c.categoryAffinity || 0) >= minAffinity);
    }
    if (Array.isArray(segments) && segments.length) {
      const set = new Set(segments);
      view = view.filter(c => set.has(c.userBrandSegment));
    }
    // Hide partial matches — creators with unmatched query criteria (v3 evidence).
    if (hidePartialMatches) {
      view = view.filter(c => !hasUnmatchedCriteria(c.evidence));
    }
    view = sortVibeResults(view, sortBy);
    allResultsRef.current = view;
    onViewChange?.({ view });
  }, [onViewChange]);

  // Dedupe by id, sort union by vibeScore desc, rerun pipeline against the
  // latest dropdown state (read from uiStateRef, not closure).
  const mergeChunk = useCallback(chunkResults => {
    if (!Array.isArray(chunkResults) || !chunkResults.length) return;
    const merged = new Map(originalRef.current.map(c => [c.id, c]));
    for (const c of chunkResults) if (!merged.has(c.id)) merged.set(c.id, c);
    originalRef.current = [...merged.values()].sort(
      (a, b) => (b.vibeScore || 0) - (a.vibeScore || 0)
    );
    const { sortBy, minAffinity, selectedSegments, hidePartialMatches } = uiStateRef.current;
    recompute({ sortBy, minAffinity, segments: selectedSegments, hidePartialMatches });
  }, [uiStateRef, recompute]);

  // Fan out per-bucket Modal calls, capped at VIBE_CHUNK_CONCURRENCY. Worker
  // pool pattern: a handful of long-running async workers each claim the next
  // unprocessed index until the queue drains. fetchId guards against a newer
  // submission stealing the slot (stale chunks discard their data writes).
  const runChunks = useCallback(async ({ vibeQuery, chunks, fetchId }) => {
    if (!chunks.length) return { anySuccess: false, anyError: false, anyNoMatches: false };
    setLoadingChunks(c => c + chunks.length);
    let anySuccess = false;
    let anyError = false;
    let anyNoMatches = false;
    const newFailed = [];
    let cursor = 0;
    const worker = async () => {
      while (cursor < chunks.length) {
        const chunk = chunks[cursor++];
        const { floor, ceiling } = chunk;
        try {
          const resp = await getDiscoverVibe(brand.id, {
            vibeQuery,
            minAffinity: floor,
            affinityCeiling: ceiling,
            topKPerBucket: VIBE_TOP_K_PER_BUCKET,
            selectedSegments: uiStateRef.current.selectedSegments,
            tagids: selectedTags.map(t => t.id),
            listids: selectedLists.map(l => l.id),
          });
          if (fetchId !== fetchIdRef.current) continue;
          if (resp?.error === 'no_matches') {
            anyNoMatches = true;
          } else if (resp?.error) {
            anyError = true;
            newFailed.push(chunk);
          } else if (Array.isArray(resp?.results)) {
            anySuccess = true;
            // Write-once capture of the response-level evidence; bump so the
            // consumer re-renders cards with the now-available set-level clauses.
            if (!disambiguationRef.current && resp.disambiguation) {
              disambiguationRef.current = resp.disambiguation;
              bump();
            }
            mergeChunk(resp.results);
          }
        } catch (err) {
          if (fetchId !== fetchIdRef.current) continue;
          anyError = true;
          newFailed.push(chunk);
        } finally {
          // Unconditional — every claimed chunk fires one finally, so
          // decrements always balance bumps even when stale chunks finish.
          setLoadingChunks(c => c - 1);
        }
      }
    };
    const poolSize = Math.min(VIBE_CHUNK_CONCURRENCY, chunks.length);
    await Promise.all(Array.from({ length: poolSize }, worker));
    if (newFailed.length && fetchId === fetchIdRef.current) {
      setFailedChunks(prev => [...prev, ...newFailed]);
    }
    return { anySuccess, anyError, anyNoMatches };
  }, [brand, mergeChunk, selectedTags, selectedLists, uiStateRef, fetchIdRef, bump]);

  const submit = useCallback(async (vibeQuery, floor) => {
    // Stale-response guard. admin axios is 0.19.2 (no signal support), so we
    // discard rather than abort. Swap to AbortController once upgraded.
    const fetchId = ++fetchIdRef.current;
    const chunks = computeVibeChunks(floor, 1.0);
    // Loaded floor ratchets optimistically to what we asked for, not what
    // came back — per-chunk failures don't invalidate coverage tracking.
    reset({ floor, query: vibeQuery });
    onLoadingChange?.(true);
    try {
      const { anySuccess, anyError, anyNoMatches } = await runChunks({ vibeQuery, chunks, fetchId });
      if (fetchId !== fetchIdRef.current) return;
      if (!anySuccess && anyError) {
        // reset() before setError() — reset() clears error state internally,
        // so setting error first would be nullified before render.
        reset();
        setError('service_unavailable');
      } else if (!anySuccess && anyNoMatches) {
        setError('no_matches');
      }
      // Partial success → render what came back.
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      reset();
      setError('service_unavailable');
    } finally {
      // Only clear loading if no newer fetch has taken over.
      if (fetchId === fetchIdRef.current) onLoadingChange?.(false);
    }
  }, [runChunks, reset, fetchIdRef, onLoadingChange]);

  // Refill is a continuation, not a new submission — reuse fetchId so any
  // still-in-flight initial chunks stay valid. No-op if the new floor is at
  // or above what's already loaded.
  const refillTo = useCallback(async newFloor => {
    if (newFloor >= loadedFloorRef.current) return;
    const fetchId = fetchIdRef.current;
    const chunks = computeVibeChunks(newFloor, loadedFloorRef.current);
    loadedFloorRef.current = newFloor;
    bump();
    const vibeQuery = activeQueryRef.current;
    if (vibeQuery) await runChunks({ vibeQuery, chunks, fetchId });
  }, [runChunks, fetchIdRef, bump]);

  // Re-fire previously-failed chunks under the active vibe query. Chunks
  // that fail again get re-added by runChunks.
  const retry = useCallback(async () => {
    if (!failedChunks.length || !activeQueryRef.current || !brand) return;
    const fetchId = fetchIdRef.current;
    const toRetry = failedChunks;
    setFailedChunks([]);
    await runChunks({ vibeQuery: activeQueryRef.current, chunks: toRetry, fetchId });
  }, [failedChunks, brand, runChunks, fetchIdRef]);

  const getDisplayedView = useCallback(() => allResultsRef.current, []);

  return {
    // Reactive read-only state
    isActive: originalRef.current.length > 0,
    error,
    loadingChunks,
    hasFailedChunks: failedChunks.length > 0,
    loadedFloor: loadedFloorRef.current,
    loadedCount: originalRef.current.length,
    // Response-level evidence shared by every hit (taste topic + geo). null
    // until the first successful chunk; consumers compose per-hit + set-level.
    disambiguation: disambiguationRef.current,
    // Operations
    submit,
    refillTo,
    recompute,
    retry,
    reset,
    getDisplayedView,
  };
};
